import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import DashboardTemplate from '../../Common/DashboardTemplate';
import DrawerMenuRight from '../../Common/DrawerMenuRight';
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  TablePagination,
  TextField,
  Typography,
} from '@material-ui/core';
import FirstPageIcon from '@material-ui/icons/FirstPage';
import LastPageIcon from '@material-ui/icons/LastPage';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { customerOrderService } from '../../../Services/CustomerOrderService';
import { formatUtcForDisplay } from '../../../utils/dateUtils';
import {
  COUNTRIES,
  US_STATES,
  isUnitedStates,
  normalizeCountryCode,
  normalizeUsStateCode,
} from '../../../utils/geo';

const styles = {
  sortableHeader: {
    cursor: 'pointer',
    userSelect: 'none',
  },
};

class Orders extends React.Component {
  state = {
    loading: true,
    orders: [],
    selectedOrder: null,
    detailOpen: false,
    detailLoading: false,
    fulfillOpen: false,
    refundOpen: false,
    autoPackage: true,
    packageWeightOz: 16,
    suggestedWeightOz: 16,
    shipPackages: [],
    packageCode: 'package',
    lengthIn: '',
    widthIn: '',
    heightIn: '',
    showManualDimensions: true,
    fulfillPrepLoading: false,
    refundReason: '',
    refundQuantities: {},
    notes: '',
    shippingForm: {
      customerName: '',
      line1: '',
      line2: '',
      city: '',
      stateOrProvince: '',
      postalCode: '',
      country: 'US',
    },
    saving: false,
    error: null,
    searchTerm: '',
    page: 0,
    rowsPerPage: 50,
    sortBy: 'placedAt',
    sortDirection: 'desc',
    actionsMenuAnchor: null,
    actionsMenuOrder: null,
  };

  componentDidMount() {
    this.loadOrders();
    const params = new URLSearchParams(this.props.location?.search || '');
    const seq = params.get('seq');
    if (seq) {
      this.openOrderBySequence(Number(seq));
    }
    const orderId = this.props.match?.params?.orderId;
    if (orderId) {
      this.openOrder(orderId);
    }
  }

  loadOrders = () => {
    this.setState({ loading: true, error: null });
    customerOrderService.getAll()
      .then((orders) => this.setState({ orders: orders || [], loading: false }))
      .catch((err) => this.setState({
        loading: false,
        error: err?.message || 'Failed to load orders',
      }));
  };

  searchOrders = (event) => {
    this.setState({ searchTerm: event.target.value, page: 0 });
  };

  handleSort = (field) => {
    const { sortBy, sortDirection } = this.state;
    this.setState({
      sortBy: field,
      sortDirection: sortBy === field && sortDirection === 'asc' ? 'desc' : 'asc',
      page: 0,
    });
  };

  handleChangePage = (event, newPage) => {
    this.setState({ page: newPage });
  };

  handleChangeRowsPerPage = (event) => {
    this.setState({ rowsPerPage: parseInt(event.target.value, 10), page: 0 });
  };

  getSortedAndFilteredOrders = () => {
    const { orders, searchTerm, sortBy, sortDirection } = this.state;
    const terms = (searchTerm || '').toLowerCase().split(' ').filter((term) => term.trim() !== '');
    let filtered = orders || [];
    if (terms.length > 0) {
      filtered = filtered.filter((order) => terms.every((term) => {
        const haystack = [
          order.orderNumber,
          order.customerName,
          order.customerEmail,
          order.status,
          order.trackingSummary,
          order.shippingMethodName,
          order.globalSequenceNumber,
        ].map((value) => String(value || '').toLowerCase());
        return haystack.some((value) => value.includes(term));
      }));
    }

    const direction = sortDirection === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const left = this.getSortValue(a, sortBy);
      const right = this.getSortValue(b, sortBy);
      if (typeof left === 'number' && typeof right === 'number') {
        return (left - right) * direction;
      }
      return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' }) * direction;
    });
  };

  getSortValue = (order, field) => {
    switch (field) {
      case 'orderNumber': return order.orderNumber || '';
      case 'placedAt': return order.placedAt ? new Date(order.placedAt).getTime() : 0;
      case 'customer': return (order.customerName || order.customerEmail || '').toLowerCase();
      case 'status': return order.status || '';
      case 'amount': return Number(order.amount || 0);
      case 'lineCount': return Number(order.lineCount || 0);
      case 'tracking': return order.trackingSummary || '';
      default: return '';
    }
  };

  openOrderBySequence = (globalSequenceNumber) => {
    customerOrderService.getAll().then((orders) => {
      const match = (orders || []).find((o) => o.globalSequenceNumber === globalSequenceNumber);
      if (match) this.openOrder(match.id);
    });
  };

  openOrder = (orderId, { openFulfill = false } = {}) => {
    this.setState({
      detailOpen: true,
      detailLoading: true,
      selectedOrder: null,
      fulfillOpen: false,
      refundOpen: false,
    });
    customerOrderService.getById(orderId)
      .then((order) => {
        this.setState({
          selectedOrder: order,
          detailLoading: false,
          notes: order.notes || '',
          shippingForm: this.buildShippingForm(order),
          packageWeightOz: order.suggestedWeightOz || 16,
          suggestedWeightOz: order.suggestedWeightOz || 16,
          refundQuantities: (order.lineItems || []).reduce((acc, line) => {
            const remaining = line.quantity - (line.quantityRefunded || 0);
            if (remaining > 0) acc[line.id] = 0;
            return acc;
          }, {}),
        });
        if (openFulfill) this.openFulfillPanel();
      })
      .catch(() => this.setState({ detailLoading: false, error: 'Failed to load order details' }));
  };

  closeDetail = () => {
    this.setState({ detailOpen: false, selectedOrder: null, fulfillOpen: false, refundOpen: false });
  };

  openFulfillPanel = () => {
    const { selectedOrder } = this.state;
    if (!selectedOrder) return;
    this.setState({
      fulfillOpen: true,
      fulfillPrepLoading: true,
      error: null,
      packageWeightOz: selectedOrder.suggestedWeightOz || this.state.suggestedWeightOz || 16,
    });
    customerOrderService.getFulfillPrep(selectedOrder.id)
      .then((prep) => {
        const packages = prep?.packages || [];
        const preferred = packages.find((p) => String(p.code).toLowerCase() === 'package') || packages[0];
        const code = preferred?.code || 'package';
        this.setState({
          fulfillPrepLoading: false,
          shipPackages: packages,
          packageCode: code,
          packageWeightOz: prep?.suggestedWeightOz || selectedOrder.suggestedWeightOz || 16,
          suggestedWeightOz: prep?.suggestedWeightOz || selectedOrder.suggestedWeightOz || 16,
          showManualDimensions: !preferred || preferred.isCustom || String(code).toLowerCase() === 'package',
          lengthIn: preferred?.lengthIn || '',
          widthIn: preferred?.widthIn || '',
          heightIn: preferred?.heightIn || '',
        });
      })
      .catch((err) => this.setState({
        fulfillPrepLoading: false,
        shipPackages: [{ code: 'package', name: 'Package (manual dimensions)', isCustom: true }],
        packageCode: 'package',
        showManualDimensions: true,
        error: err?.response?.data || err?.message || 'Failed to load ShipStation packages',
      }));
  };

  handlePackageCodeChange = (code) => {
    const selected = (this.state.shipPackages || []).find((p) => p.code === code);
    const manual = !selected || selected.isCustom || String(code).toLowerCase() === 'package';
    this.setState({
      packageCode: code,
      showManualDimensions: manual,
      lengthIn: selected?.lengthIn || (manual ? this.state.lengthIn : ''),
      widthIn: selected?.widthIn || (manual ? this.state.widthIn : ''),
      heightIn: selected?.heightIn || (manual ? this.state.heightIn : ''),
    });
  };

  handleFulfill = () => {
    const {
      selectedOrder,
      autoPackage,
      packageWeightOz,
      packageCode,
      lengthIn,
      widthIn,
      heightIn,
      showManualDimensions,
    } = this.state;
    if (!selectedOrder) return;

    const weight = Number(packageWeightOz);
    if (!weight || weight <= 0) {
      this.setState({ error: 'Enter a package weight in ounces.' });
      return;
    }

    const length = Number(lengthIn);
    const width = Number(widthIn);
    const height = Number(heightIn);
    const needsDims = showManualDimensions || String(packageCode).toLowerCase() === 'package';
    if (needsDims && (!(length > 0) || !(width > 0) || !(height > 0))) {
      this.setState({ error: 'Enter package length, width, and height in inches.' });
      return;
    }

    if (!window.confirm(
      `Fulfill order ${selectedOrder.orderNumber}?\n\nThis creates a ShipStation label/tracking and emails the customer that their order is preparing to ship.`
    )) return;

    this.setState({ saving: true, error: null });
    customerOrderService.fulfill(selectedOrder.id, {
      autoPackage,
      packages: [{
        weightOz: weight,
        carrierCode: selectedOrder.shippingCarrierCode,
        serviceCode: selectedOrder.shippingServiceCode,
        packageCode: packageCode || 'package',
        lengthIn: length > 0 ? length : null,
        widthIn: width > 0 ? width : null,
        heightIn: height > 0 ? height : null,
      }],
    })
      .then((order) => {
        this.setState({
          selectedOrder: order,
          fulfillOpen: false,
          saving: false,
        });
        this.loadOrders();
      })
      .catch((err) => this.setState({
        saving: false,
        error: err?.response?.data || err?.message || 'Fulfillment failed',
      }));
  };

  handleRefund = (full = false) => {
    const { selectedOrder, refundQuantities, refundReason } = this.state;
    if (!selectedOrder) return;
    this.setState({ saving: true, error: null });
    const promise = full
      ? customerOrderService.refundFull(selectedOrder.id, { reason: refundReason })
      : customerOrderService.refund(selectedOrder.id, {
        reason: refundReason,
        lineRefunds: Object.entries(refundQuantities)
          .filter(([, qty]) => Number(qty) > 0)
          .map(([lineItemId, quantity]) => ({ lineItemId, quantity: Number(quantity) })),
      });
    promise
      .then((order) => {
        this.setState({
          selectedOrder: order,
          refundOpen: false,
          saving: false,
        });
        this.loadOrders();
      })
      .catch((err) => this.setState({
        saving: false,
        error: err?.message || 'Refund failed',
      }));
  };

  openActionsMenu = (event, order) => {
    event.stopPropagation();
    this.setState({ actionsMenuAnchor: event.currentTarget, actionsMenuOrder: order });
  };

  closeActionsMenu = () => {
    this.setState({ actionsMenuAnchor: null, actionsMenuOrder: null });
  };

  canFulfillOrder = (order) => order && (order.status === 'Pending' || order.status === 'PartiallyRefunded')
    && !order.fulfilledAt;

  canMarkShipped = (order) => order
    && !order.shippedAt
    && order.status !== 'Cancelled'
    && order.status !== 'Shipped'
    && (order.status === 'Fulfilled' || !!order.fulfilledAt || !!order.trackingSummary);

  canRefundOrder = (order) => order && order.status !== 'Cancelled'
    && Number(order.amount || 0) > Number(order.refundedAmount || 0);

  markOrderShipped = (order) => {
    if (!order || !this.canMarkShipped(order)) return;
    const label = order.orderNumber || order.id;
    if (!window.confirm(
      `Mark order ${label} as shipped?\n\nThis emails the customer that their order has shipped (with tracking and order summary).`
    )) return;

    this.setState({ saving: true, error: null });
    customerOrderService.markShipped(order.id)
      .then((updated) => {
        this.setState((prev) => ({
          saving: false,
          selectedOrder: prev.selectedOrder?.id === updated.id ? updated : prev.selectedOrder,
        }));
        this.loadOrders();
      })
      .catch((err) => this.setState({
        saving: false,
        error: err?.response?.data || err?.message || 'Failed to mark order shipped',
      }));
  };

  refundOrderQuick = (order) => {
    if (!order || !this.canRefundOrder(order)) return;
    const label = order.orderNumber || order.id;
    if (!window.confirm(
      `Fully refund order ${label}?\n\nThis will refund the remaining amount via Stripe and update linked sales logs (Cancelled / split).`
    )) return;

    this.setState({ saving: true, error: null });
    customerOrderService.refundFull(order.id, { reason: 'Admin refund from orders list' })
      .then((updated) => {
        this.setState((prev) => ({
          saving: false,
          selectedOrder: prev.selectedOrder?.id === updated.id ? updated : prev.selectedOrder,
        }));
        this.loadOrders();
      })
      .catch((err) => this.setState({
        saving: false,
        error: err?.response?.data || err?.message || 'Refund failed',
      }));
  };

  deleteOrder = (order) => {
    if (!order) return;
    const label = order.orderNumber || order.id;
    if (!window.confirm(
      `DELETE order ${label}?\n\nThis permanently removes the order, shipments, refunds, and linked sales logs from the database.\nIt does NOT refund Stripe or void shipping labels.`
    )) return;

    this.setState({ saving: true, error: null });
    customerOrderService.delete(order.id)
      .then(() => {
        this.setState((prev) => ({
          saving: false,
          detailOpen: prev.selectedOrder?.id === order.id ? false : prev.detailOpen,
          selectedOrder: prev.selectedOrder?.id === order.id ? null : prev.selectedOrder,
        }));
        this.loadOrders();
      })
      .catch((err) => this.setState({
        saving: false,
        error: err?.response?.data || err?.message || 'Failed to delete order',
      }));
  };

  handleActionsMenuSelect = (action) => {
    const order = this.state.actionsMenuOrder;
    this.closeActionsMenu();
    if (!order) return;
    if (action === 'fulfill') this.openOrder(order.id, { openFulfill: true });
    else if (action === 'markShipped') this.markOrderShipped(order);
    else if (action === 'refund') this.refundOrderQuick(order);
    else if (action === 'delete') this.deleteOrder(order);
  };

  saveNotes = () => {
    const { selectedOrder, notes } = this.state;
    if (!selectedOrder) return;
    customerOrderService.updateNotes(selectedOrder.id, notes)
      .then(() => this.openOrder(selectedOrder.id))
      .catch(() => this.setState({ error: 'Failed to save notes' }));
  };

  buildShippingForm = (order) => {
    const country = normalizeCountryCode(order?.shippingCountry) || 'US';
    return {
      customerName: order?.customerName || '',
      line1: order?.shippingLine1 || '',
      line2: order?.shippingLine2 || '',
      city: order?.shippingCity || '',
      stateOrProvince: isUnitedStates(country) ? (normalizeUsStateCode(order?.shippingState) || '') : '',
      postalCode: order?.shippingPostalCode || '',
      country,
    };
  };

  setShippingField = (field, value) => {
    this.setState((prev) => {
      const shippingForm = { ...prev.shippingForm, [field]: value };
      if (field === 'country' && !isUnitedStates(value)) {
        shippingForm.stateOrProvince = '';
      }
      return { shippingForm };
    });
  };

  saveShippingAddress = () => {
    const { selectedOrder, shippingForm } = this.state;
    if (!selectedOrder) return;
    this.setState({ saving: true, error: null });
    customerOrderService.updateShippingAddress(selectedOrder.id, shippingForm)
      .then((order) => this.setState({
        selectedOrder: order,
        shippingForm: this.buildShippingForm(order),
        saving: false,
      }))
      .catch((err) => this.setState({
        saving: false,
        error: err?.response?.data?.message || err?.message || 'Failed to save shipping address',
      }));
  };

  renderStatusChip(status) {
    const colors = {
      Pending: '#f59e0b',
      Fulfilled: '#0ea5e9',
      Shipped: '#16a34a',
      PartiallyRefunded: '#6366f1',
      Cancelled: '#dc2626',
    };
    return (
      <span style={{ color: colors[status] || '#333', fontWeight: 600 }}>
        {status || '—'}
      </span>
    );
  }

  renderSortHeader = (field, label, minWidth = 0) => {
    const { classes } = this.props;
    const { sortBy, sortDirection } = this.state;
    return (
      <th className={classes.sortableHeader} onClick={() => this.handleSort(field)} style={{ minWidth }}>
        {label}
        {sortBy === field && (
          <FontAwesomeIcon
            icon={sortDirection === 'asc' ? faChevronUp : faChevronDown}
            size="sm"
            style={{ marginLeft: 8 }}
          />
        )}
      </th>
    );
  };

  renderPagination = (count) => (
    <center>
      <TablePagination
        rowsPerPageOptions={[25, 50, 100, 200]}
        colSpan={8}
        count={count}
        rowsPerPage={this.state.rowsPerPage}
        page={this.state.page}
        slotProps={{
          select: { 'aria-label': 'rows per page' },
          actions: {
            showFirstButton: true,
            showLastButton: true,
            slots: {
              firstPageIcon: FirstPageIcon,
              lastPageIcon: LastPageIcon,
              nextPageIcon: ChevronRightIcon,
              backPageIcon: ChevronLeftIcon,
            },
          },
        }}
        onPageChange={this.handleChangePage}
        onRowsPerPageChange={this.handleChangeRowsPerPage}
      />
    </center>
  );

  render() {
    const {
      loading,
      selectedOrder,
      detailOpen,
      detailLoading,
      fulfillOpen,
      refundOpen,
      autoPackage,
      packageWeightOz,
      suggestedWeightOz,
      shipPackages,
      packageCode,
      lengthIn,
      widthIn,
      heightIn,
      showManualDimensions,
      fulfillPrepLoading,
      refundReason,
      refundQuantities,
      notes,
      shippingForm,
      saving,
      error,
      searchTerm,
      page,
      rowsPerPage,
      actionsMenuAnchor,
      actionsMenuOrder,
    } = this.state;

    const filteredOrders = this.getSortedAndFilteredOrders();
    const paginatedOrders = filteredOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
      <DashboardTemplate title="Customer Orders" onSyncDatabase={this.loadOrders}>
        <div className="main-dashboard-root">
          <div className="container" style={{ maxWidth: 1400 }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%', marginBottom: 12 }}>
              <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
                <TextField
                  label="Search orders..."
                  variant="outlined"
                  value={searchTerm}
                  onChange={this.searchOrders}
                  style={{ width: 400 }}
                />
              </div>
            </div>
            {error && <Typography color="error" gutterBottom>{error}</Typography>}
            {this.renderPagination(filteredOrders.length)}
            {loading ? (
              <div style={{ textAlign: 'center', padding: 40 }}><CircularProgress /></div>
            ) : (
              <div className="parts-table-container">
                <table className="parts-table">
                  <thead>
                    <tr>
                      {this.renderSortHeader('orderNumber', 'Order #', 50)}
                      {this.renderSortHeader('placedAt', 'Date', 100)}
                      {this.renderSortHeader('customer', 'Customer', 150)}
                      {this.renderSortHeader('status', 'Status', 75)}
                      {this.renderSortHeader('amount', 'Total', 75)}
                      {this.renderSortHeader('lineCount', 'Lines', 30)}
                      {this.renderSortHeader('tracking', 'Tracking', 125)}
                      <th style={{ minWidth: 48 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedOrders.map((order) => (
                      <tr
                        key={order.id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => this.openOrder(order.id)}
                      >
                        <td>
                          <span style={{ fontWeight: 'bold' }}>{order.orderNumber}</span>
                        </td>
                        <td>{formatUtcForDisplay(order.placedAt)}</td>
                        <td className="text-left">{order.customerName || order.customerEmail || '—'}</td>
                        <td>{this.renderStatusChip(order.status)}</td>
                        <td>${Number(order.amount || 0).toFixed(2)}</td>
                        <td>{order.lineCount}</td>
                        <td>{order.trackingSummary || '—'}</td>
                        <td onClick={(e) => e.stopPropagation()} style={{ width: 48, textAlign: 'center' }}>
                          <IconButton
                            size="small"
                            aria-label="Order actions"
                            onClick={(e) => this.openActionsMenu(e, order)}
                            disabled={saving}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {this.renderPagination(filteredOrders.length)}

            <Menu
              anchorEl={actionsMenuAnchor}
              open={Boolean(actionsMenuAnchor)}
              onClose={this.closeActionsMenu}
              keepMounted
            >
              <MenuItem
                disabled={!this.canFulfillOrder(actionsMenuOrder) || saving}
                onClick={() => this.handleActionsMenuSelect('fulfill')}
              >
                Fulfill
              </MenuItem>
              <MenuItem
                disabled={!this.canMarkShipped(actionsMenuOrder) || saving}
                onClick={() => this.handleActionsMenuSelect('markShipped')}
              >
                Mark shipped
              </MenuItem>
              <MenuItem
                disabled={!this.canRefundOrder(actionsMenuOrder) || saving}
                onClick={() => this.handleActionsMenuSelect('refund')}
              >
                Refund
              </MenuItem>
              <MenuItem
                disabled={saving}
                onClick={() => this.handleActionsMenuSelect('delete')}
                style={{ color: '#dc2626' }}
              >
                Delete
              </MenuItem>
            </Menu>
          </div>
        </div>

        {detailOpen && (
          <DrawerMenuRight
            title={selectedOrder ? `Order ${selectedOrder.orderNumber}` : 'Order details'}
            open={detailOpen}
            onClose={this.closeDetail}
            customWidth={720}
          >
            {detailLoading ? (
              <CircularProgress />
            ) : selectedOrder && (
              <div style={{ padding: 20 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                {this.canFulfillOrder(selectedOrder) && (
                  <Button
                    variant="contained"
                    color="primary"
                    className="success-button-lg"
                    onClick={this.openFulfillPanel}
                  >
                    Fulfill
                  </Button>
                )}
                {this.canMarkShipped(selectedOrder) && (
                  <Button
                    variant="contained"
                    color="primary"
                    className="success-button-lg"
                    disabled={saving}
                    onClick={() => this.markOrderShipped(selectedOrder)}
                  >
                    Mark shipped
                  </Button>
                )}
                {selectedOrder.status !== 'Cancelled' && (
                  <Button
                    variant="outlined"
                    onClick={() => this.setState({ refundOpen: true })}
                  >
                    Refund
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="secondary"
                  disabled={saving}
                  onClick={() => this.deleteOrder(selectedOrder)}
                >
                  Delete
                </Button>
              </div>
                <Typography variant="subtitle1" gutterBottom>
                  {selectedOrder.customerName}
                  {' '}
                  (
                  {selectedOrder.customerEmail}
                  )
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Status:
                  {' '}
                  {this.renderStatusChip(selectedOrder.status)}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Method:
                  {' '}
                  {selectedOrder.shippingMethodName || '—'}
                  {selectedOrder.shippingCarrierCode ? ` (${selectedOrder.shippingCarrierCode} / ${selectedOrder.shippingServiceCode})` : ''}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Subtotal ${Number(selectedOrder.subtotal || 0).toFixed(2)}
                  {' · '}
                  Shipping ${Number(selectedOrder.shippingTotal || 0).toFixed(2)}
                  {' · '}
                  Discount -${Number(selectedOrder.discountTotal || 0).toFixed(2)}
                  {' · '}
                  Total ${Number(selectedOrder.amount || 0).toFixed(2)}
                </Typography>

                <Typography variant="subtitle2" style={{ marginTop: 16 }}>Shipping address</Typography>
                <TextField
                  label="Name"
                  value={shippingForm.customerName}
                  onChange={(e) => this.setShippingField('customerName', e.target.value)}
                  fullWidth
                  variant="outlined"
                  disabled={selectedOrder.status !== 'Pending' && selectedOrder.status !== 'PartiallyRefunded'}
                  style={{ marginTop: 8, marginBottom: 8 }}
                />
                <TextField
                  label="Street"
                  value={shippingForm.line1}
                  onChange={(e) => this.setShippingField('line1', e.target.value)}
                  fullWidth
                  variant="outlined"
                  disabled={selectedOrder.status !== 'Pending' && selectedOrder.status !== 'PartiallyRefunded'}
                  style={{ marginBottom: 8 }}
                />
                <TextField
                  label="Apt / suite"
                  value={shippingForm.line2}
                  onChange={(e) => this.setShippingField('line2', e.target.value)}
                  fullWidth
                  variant="outlined"
                  disabled={selectedOrder.status !== 'Pending' && selectedOrder.status !== 'PartiallyRefunded'}
                  style={{ marginBottom: 8 }}
                />
                <TextField
                  label="City"
                  value={shippingForm.city}
                  onChange={(e) => this.setShippingField('city', e.target.value)}
                  fullWidth
                  variant="outlined"
                  disabled={selectedOrder.status !== 'Pending' && selectedOrder.status !== 'PartiallyRefunded'}
                  style={{ marginBottom: 8 }}
                />
                <FormControl fullWidth variant="outlined" style={{ marginBottom: 8 }}>
                  <InputLabel>Country</InputLabel>
                  <Select
                    value={shippingForm.country || ''}
                    onChange={(e) => this.setShippingField('country', e.target.value)}
                    label="Country"
                    disabled={selectedOrder.status !== 'Pending' && selectedOrder.status !== 'PartiallyRefunded'}
                  >
                    {COUNTRIES.map((country) => (
                      <MenuItem key={country.code} value={country.code}>{country.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {isUnitedStates(shippingForm.country) && (
                  <FormControl fullWidth variant="outlined" style={{ marginBottom: 8 }}>
                    <InputLabel>State</InputLabel>
                    <Select
                      value={shippingForm.stateOrProvince || ''}
                      onChange={(e) => this.setShippingField('stateOrProvince', e.target.value)}
                      label="State"
                      disabled={selectedOrder.status !== 'Pending' && selectedOrder.status !== 'PartiallyRefunded'}
                    >
                      {US_STATES.map((state) => (
                        <MenuItem key={state.code} value={state.code}>{state.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <TextField
                  label="Postal code"
                  value={shippingForm.postalCode}
                  onChange={(e) => this.setShippingField('postalCode', e.target.value)}
                  fullWidth
                  variant="outlined"
                  disabled={selectedOrder.status !== 'Pending' && selectedOrder.status !== 'PartiallyRefunded'}
                  style={{ marginBottom: 8 }}
                />
                {(selectedOrder.status === 'Pending' || selectedOrder.status === 'PartiallyRefunded') && (
                  <Button
                    variant="contained"
                    color="primary"
                    className="success-button-lg"
                    onClick={this.saveShippingAddress}
                    disabled={saving}
                    style={{ marginBottom: 16 }}
                  >
                    Save address
                  </Button>
                )}

                <div className="parts-table-container" style={{ marginTop: 16, marginBottom: 16, maxHeight: 280 }}>
                  <table className="parts-table">
                    <thead>
                      <tr>
                        <th style={{minWidth: 30}}>#</th>
                        <th className="description-ellipsis">Item</th>
                        <th style={{minWidth: 30}}>Qty</th>
                        <th style={{minWidth: 75}}>Price</th>
                        <th style={{minWidth: 85}}>Sales log</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedOrder.lineItems || []).map((line) => (
                        <tr key={line.id}>
                          <td>{line.lineNumber}</td>
                          <td className="text-left description-ellipsis">{line.description}</td>
                          <td>{line.quantity}</td>
                          <td>${Number(line.unitPrice || 0).toFixed(2)}</td>
                          <td>
                            {line.compositeSalesLogNumber && selectedOrder.globalSequenceNumber ? (
                              <a href={`/admin/sales/${selectedOrder.globalSequenceNumber}`}>
                                {line.compositeSalesLogNumber}
                              </a>
                            ) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {(selectedOrder.shipments || []).length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <Typography variant="subtitle2">Shipments</Typography>
                    {(selectedOrder.shipments || []).map((s) => (
                      <div key={s.id} style={{ marginTop: 8 }}>
                        <Typography variant="body2">
                          Package
                          {' '}
                          {s.packageIndex}
                          :
                          {' '}
                          {s.trackingNumber || 'No tracking'}
                          {s.labelPdfUrl && (
                            <>
                              {' '}
                              <a href={s.labelPdfUrl} target="_blank" rel="noopener noreferrer">Print label</a>
                            </>
                          )}
                        </Typography>
                      </div>
                    ))}
                  </div>
                )}

                <TextField
                  label="Admin notes"
                  value={notes}
                  onChange={(e) => this.setState({ notes: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  style={{ marginBottom: 12 }}
                />
                <Button onClick={this.saveNotes}
                    variant="contained"
                    color="primary"
                    className="success-button-lg"
                    disabled={saving}
                    style={{ marginBottom: 16 }}>Save notes</Button>
                {fulfillOpen && (
                  <div style={{ marginTop: 24, borderTop: '1px solid #ddd', paddingTop: 16 }}>
                    <Typography variant="subtitle2" gutterBottom>Fulfill order</Typography>
                    <Typography variant="body2" style={{ color: '#666', marginBottom: 12 }}>
                      Creates a ShipStation label/tracking and emails &quot;Your order is preparing to ship!&quot;
                      with the order summary. Use Mark shipped later for the shipped email.
                    </Typography>
                    {fulfillPrepLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <>
                        <TextField
                          label="Package weight (oz)"
                          type="number"
                          variant="outlined"
                          value={packageWeightOz}
                          onChange={(e) => this.setState({ packageWeightOz: e.target.value })}
                          helperText={`Suggested from product weights: ${suggestedWeightOz} oz`}
                          fullWidth
                          style={{ marginBottom: 12 }}
                        />
                        <FormControl variant="outlined" fullWidth style={{ marginBottom: 12 }}>
                          <InputLabel>ShipStation package</InputLabel>
                          <Select
                            label="ShipStation package"
                            value={packageCode || 'package'}
                            onChange={(e) => this.handlePackageCodeChange(e.target.value)}
                          >
                            {(shipPackages || []).map((pkg) => (
                              <MenuItem key={pkg.code} value={pkg.code}>
                                {pkg.name || pkg.code}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {(showManualDimensions || String(packageCode).toLowerCase() === 'package') && (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
                            <TextField
                              label="Length (in)"
                              type="number"
                              variant="outlined"
                              value={lengthIn}
                              onChange={(e) => this.setState({ lengthIn: e.target.value })}
                            />
                            <TextField
                              label="Width (in)"
                              type="number"
                              variant="outlined"
                              value={widthIn}
                              onChange={(e) => this.setState({ widthIn: e.target.value })}
                            />
                            <TextField
                              label="Height (in)"
                              type="number"
                              variant="outlined"
                              value={heightIn}
                              onChange={(e) => this.setState({ heightIn: e.target.value })}
                            />
                          </div>
                        )}
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={autoPackage}
                              onChange={(e) => this.setState({ autoPackage: e.target.checked })}
                              color="primary"
                            />
                          )}
                          label="Auto-package assembly/subassembly lines"
                        />
                        <div style={{ marginTop: 12 }}>
                          <Button
                            variant="contained"
                            color="primary"
                            className="success-button-lg"
                            disabled={saving}
                            onClick={this.handleFulfill}
                          >
                            {saving ? 'Processing…' : 'Create label & fulfill'}
                          </Button>
                          <Button onClick={() => this.setState({ fulfillOpen: false })} style={{ marginLeft: 8 }}>
                            Cancel
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {refundOpen && (
                  <div style={{ marginTop: 24, borderTop: '1px solid #ddd', paddingTop: 16 }}>
                    <Typography variant="subtitle2" gutterBottom>Refund</Typography>
                    <TextField
                      label="Reason"
                      value={refundReason}
                      onChange={(e) => this.setState({ refundReason: e.target.value })}
                      fullWidth
                      variant="outlined"
                      style={{ marginBottom: 12 }}
                    />
                    {(selectedOrder.lineItems || []).map((line) => {
                      const remaining = line.quantity - (line.quantityRefunded || 0);
                      if (remaining <= 0) return null;
                      return (
                        <div key={line.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <Typography variant="body2" style={{ flex: 1 }}>
                            {line.description}
                            {' '}
                            (max
                            {' '}
                            {remaining}
                            )
                          </Typography>
                          <TextField
                            type="number"
                            size="small"
                            variant="outlined"
                            inputProps={{ min: 0, max: remaining }}
                            value={refundQuantities[line.id] || 0}
                            onChange={(e) => this.setState({
                              refundQuantities: {
                                ...refundQuantities,
                                [line.id]: e.target.value,
                              },
                            })}
                            style={{ width: 80 }}
                          />
                        </div>
                      );
                    })}
                    <div style={{ marginTop: 12 }}>
                      <Button
                        variant="contained"
                        color="secondary"
                        disabled={saving}
                        onClick={() => this.handleRefund(false)}
                      >
                        Partial refund
                      </Button>
                      <Button
                        variant="outlined"
                        color="secondary"
                        disabled={saving}
                        onClick={() => this.handleRefund(true)}
                        style={{ marginLeft: 8 }}
                      >
                        Full refund
                      </Button>
                      <Button onClick={() => this.setState({ refundOpen: false })} style={{ marginLeft: 8 }}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DrawerMenuRight>
        )}
      </DashboardTemplate>
    );
  }
}

Orders.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(Orders);
